/**
 * Cómo vuelve el usuario al túnel después de autorizar una plataforma.
 *
 * Google, TikTok y LinkedIn autorizan navegando fuera de la aplicación, así que la vuelta la maneja
 * una pantalla de callback que existía mucho antes que el túnel y que siempre mandaba a la pantalla
 * de conexiones. En vez de cambiarle el destino —lo que se llevaría puesto el camino clásico, que
 * sigue vivo para las agencias que no entran al túnel— el túnel deja anotado a dónde quiere volver
 * y el callback lo respeta si lo encuentra.
 *
 * La nota se consume UNA sola vez y caduca: si alguien empieza a conectar una plataforma, abandona
 * el túnel y vuelve a la aplicación por otro lado, no puede quedar una nota vieja desviándolo a un
 * recorrido que ya no está haciendo.
 */

const KEY = 'ob_oauth_return';
// Una hora, no quince minutos: la verificación de Google (passkey por Bluetooth, 2FA) puede tomar
// más que eso, y al vencer la nota el callback devolvía al usuario al Dashbo clásico en vez de al
// túnel (card dJxYEHZ9, 24/09). El resguardo contra una nota vieja no es el reloj sino
// `isValidContext`: fuera del túnel y de `/connect-*` la nota se descarta igual.
const MAX_AGE_MS = 60 * 60 * 1000;

export function setOauthReturn(returnTo) {
    try {
        window.sessionStorage.setItem(KEY, JSON.stringify({returnTo, at: Date.now()}));
    } catch (error) {
        // Sin sessionStorage el túnel sigue funcionando: la vuelta cae en la pantalla clásica de
        // conexiones, que es exactamente lo que pasaba antes de que existiera el túnel.
    }
}

/** La nota, si sigue vigente. La borra siempre: usada o vencida, no debe sobrevivir a esta lectura. */
export function consumeOauthReturn() {
    const returnTo = readOauthReturn();
    clearOauthReturn();
    return returnTo;
}

export function clearOauthReturn() {
    try {
        window.sessionStorage.removeItem(KEY);
    } catch (error) {
        // Nada que limpiar.
    }
}

/**
 * A dónde tiene que ir el callback de OAuth.
 *
 * Sin nota del túnel devuelve el destino de siempre, intacto — es lo que hace que el camino clásico
 * no se entere de que esto existe. Con nota, manda al túnel conservando la parte de la query que el
 * callback venía a comunicar (qué canal se conectó, o que falló), que es lo que la pantalla de
 * plataformas lee para saber qué mostrar.
 */
export function oauthDestination(classicPath) {
    return destinationOf(consumeOauthReturn(), classicPath);
}

/** El mismo destino, para el camino de error: el túnel lo pinta en la tarjeta del canal. */
export function oauthErrorDestination(classicPath, channel, reason) {
    return errorDestinationOf(consumeOauthReturn(), classicPath, channel, reason);
}

/**
 * Los mismos dos destinos, pero a partir de una nota que el llamador YA leyó.
 *
 * Existen porque el intercambio del código es asincrónico y la nota puede morirse en el medio: el
 * marco del túnel la borra en su limpieza (es su forma de decir "el usuario dejó el recorrido"), y
 * las ramas del callback que devuelven al usuario al túnel montan justamente ese marco. Con la
 * lectura hecha al final —dentro del `.then()` del POST— la secuencia real era:
 *
 *   1. el callback vuelve de Google y empieza el intercambio;
 *   2. una segunda pasada del componente ve el intercambio en curso y navega al túnel con la nota;
 *   3. el marco del túnel monta y su limpieza BORRA la nota;
 *   4. el POST del paso 1 contesta, lee la nota, no la encuentra, y manda al usuario a la pantalla
 *      clásica de conexiones — encima del túnel al que acababa de volver.
 *
 * Leerla al montar y arrastrar ese valor deja el destino decidido antes de que nada pueda pisarlo,
 * que es lo que corresponde a un dato de un solo uso: se lee al empezar, no al terminar.
 */
export function destinationOf(returnTo, classicPath) {
    if (!returnTo) return classicPath;

    const queryStart = classicPath.indexOf('?');
    const query = queryStart === -1 ? '' : classicPath.slice(queryStart);
    return `${returnTo}${query}`;
}

/**
 * `reason` es el CÓDIGO del error, no su texto: el túnel tiene copy propio para cada uno.
 *
 * El mensaje del backend no puede viajar por acá —puede nombrar a otra agencia, y ésta es una URL
 * que el usuario ve— pero sin ningún dato la tarjeta sólo podía decir «suele ser un permiso que
 * falta», que en el caso más frecuente (la cuenta de Google no tiene ninguna cuenta de Google Ads)
 * manda a buscar un permiso que nadie tiene que dar. El código es un enum del backend: no lleva
 * información de nadie.
 */
export function errorDestinationOf(returnTo, classicPath, channel, reason) {
    if (!returnTo) return classicPath;
    const motivo = reason ? `&oauth_reason=${encodeURIComponent(reason)}` : '';
    return `${returnTo}?oauth_error=${encodeURIComponent(channel)}${motivo}`;
}

/**
 * La nota, sin consumirla. Existe para las ramas del callback que se ejecutan más de una vez.
 *
 * El callback de Google tiene dos guards contra el doble intercambio del código, y los dos navegan.
 * Si esas ramas consumieran la nota, el segundo pasaje ya no la encontraría y mandaría al usuario al
 * camino clásico en el medio del recorrido.
 */
export function peekOauthReturn() {
    return readOauthReturn();
}

/**
 * Los dos únicos lugares donde la nota significa algo: el túnel, que es quien la escribe, y las
 * pantallas de vuelta de OAuth (`/connect-*`), que son quienes la leen.
 *
 * Meta no aparece en esa lista: autoriza en un popup y nunca sale del paso, así que su destino se
 * decide en la propia tarjeta sin consultar nada — ver `PlatformCard.js`. La rama `/onboarding/*`
 * se conserva igual, para que leerla desde el recorrido siga siendo válido si algún día hace falta.
 *
 * Fuera de ahí la nota está vencida aunque el reloj no lo diga. El desmontaje del marco del túnel la
 * limpia al salir por navegación, pero **no** si el usuario se va recargando o escribiendo una URL:
 * ahí quedaban hasta quince minutos en los que cualquier conexión hecha desde la pantalla clásica lo
 * depositaba en un recorrido que ya había abandonado, y sin los avisos de la pantalla en la que
 * estaba. El criterio de "dónde estoy" cubre esos caminos también, porque no depende de que ningún
 * componente llegue a desmontarse.
 */
function isValidContext() {
    const path = window.location?.pathname || '';
    return /^\/onboarding(\/|$)/.test(path) || LINE_WIZARD_PATH.test(path) || /^\/connect-/.test(path);
}

// El wizard «Nueva línea de control» (card CvdQbnl2) también autoriza plataformas desde su paso de
// cuentas («Vincular más cuentas»), así que es el otro recorrido al que la nota puede devolver.
const LINE_WIZARD_PATH = /^\/lines\/new(\/|$)/;

function readOauthReturn() {
    try {
        if (!isValidContext()) {
            clearOauthReturn();
            return null;
        }
        const raw = window.sessionStorage.getItem(KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed?.returnTo) return null;
        if (Date.now() - (parsed.at || 0) > MAX_AGE_MS) return null;
        // Sólo se vuelve a un paso del propio recorrido: la nota vive en un almacenamiento que
        // cualquier código de la pestaña puede escribir, y un destino arbitrario convertiría esto en
        // una redirección abierta.
        if (!isTunnelPath(parsed.returnTo)) return null;
        return parsed.returnTo;
    } catch (error) {
        return null;
    }
}

function isTunnelPath(returnTo) {
    return typeof returnTo === 'string' && (/^\/onboarding(\/|$)/.test(returnTo) || LINE_WIZARD_PATH.test(returnTo));
}
